import React, { useContext, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

import { Ability } from '@casl/ability'

import { PlayControler } from 'components/components/PlayControler/PlayControler'

import style from './css/player.module.scss'

export interface IUserInfo {
  group_id?: number
  name?: string
  phone_number?: string
}

interface IAuthContext {
  jwt: string | undefined
  userInfo: IUserInfo | undefined
}

interface IMediaContext {
  isBig: boolean
  isSmallMax: boolean
  isBigMax: boolean
  isWrap: boolean
}

interface ICompatibleRouteContext {
  pathname: string
  search?: string
  navigate: (pathName: string, options?: { replace: boolean }) => void
}

interface IAuthProviderProps {
  auth: IAuthContext
}

interface IAbilityProviderProps {
  ability: Ability
}

interface IMediaProviderProps {
  media: IMediaContext
}

interface ICompatibleRouteProviderProps {
  CompatibleRoute: ICompatibleRouteContext
}

interface IChartMaskContentProviderProps {
  maskContent: React.ReactElement | ((chartInfo: any) => React.ReactElement)
}

interface IPlayerContext {
  setAudioSrc: React.Dispatch<React.SetStateAction<string | undefined>>
  setAudioQueue: React.Dispatch<React.SetStateAction<string[]>>
  playingRCIDRef: React.MutableRefObject<number | undefined>
}

interface IChartMaskContentContext {
  maskContent: React.ReactElement | ((chartInfo: any) => React.ReactElement)
}

const AuthConetx = React.createContext<IAuthContext | undefined>(undefined)

const AbilityContext = React.createContext<Ability>(new Ability([]))

const MediaContext = React.createContext<IMediaContext | undefined>(undefined)

const CompatibleRouteContext = React.createContext<ICompatibleRouteContext | undefined>(undefined)

const PlayerContext = React.createContext<IPlayerContext | undefined>(undefined)

const ChartMaskContentContext = React.createContext<IChartMaskContentContext>({ maskContent: <></> })

const useAuth = () => {
  const context = useContext(AuthConetx)
  if (!context) {
    throw new Error()
  } else {
    return context
  }
}

const useAbility = () => useContext(AbilityContext)

const useMedia = () => {
  const context = useContext(MediaContext)
  if (!context) {
    throw new Error()
  } else {
    return context
  }
}

const useCompatibleRoute = () => {
  const context = useContext(CompatibleRouteContext)
  if (!context) {
    throw new Error()
  } else {
    return context
  }
}

const usePlayer = () => {
  const context = useContext(PlayerContext)

  if (!context) {
    throw new Error()
  }

  const { setAudioSrc, setAudioQueue, playingRCIDRef } = context

  const IDRef = useRef(new Date().getTime())

  useEffect(
    () => () => {
      if (playingRCIDRef.current === IDRef.current) {
        setAudioSrc(void 0)
      }
    },
    [playingRCIDRef, setAudioSrc]
  )

  const play = (src: string, audioSrcAll: Array<string>) => {
    playingRCIDRef.current = IDRef.current

    setAudioSrc(src)

    setAudioQueue(audioSrcAll)
  }

  return { play }
}

const useChartMaskContent = () => useContext(ChartMaskContentContext)

const AuthProvider: React.FC<IAuthProviderProps> = ({ children, auth }) => {
  return <AuthConetx.Provider value={auth}>{children}</AuthConetx.Provider>
}

const AbilityProvider: React.FC<IAbilityProviderProps> = ({ children, ability }) => {
  return <AbilityContext.Provider value={ability}>{children}</AbilityContext.Provider>
}

const MediaProvider: React.FC<IMediaProviderProps> = ({ children, media }) => {
  return <MediaContext.Provider value={media}>{children}</MediaContext.Provider>
}

const CompatibleRouteProvider: React.FC<ICompatibleRouteProviderProps> = ({ children, CompatibleRoute }) => {
  return <CompatibleRouteContext.Provider value={CompatibleRoute}>{children}</CompatibleRouteContext.Provider>
}

const PlayerProvider: React.FC = ({ children }) => {
  const playingRCIDRef = useRef<number>()

  const [audioSrc, setAudioSrc] = useState<string>()

  const [audioQueue, setAudioQueue] = useState<string[]>([])

  const handleEnded = () => {
    const index = audioQueue.findIndex(item => item === audioSrc)
    if (index > -1 && index < audioQueue.length - 1) {
      setAudioSrc(audioQueue[index + 1])
    }
  }

  const playerElement = audioSrc ? (
    createPortal(
      <div className={style['player-wrap']}>
        <PlayControler
          src={audioSrc}
          onEnded={handleEnded}
          className={style['player']}
          sliderProps={{
            railStyle: { background: 'rgba(255, 255, 255, 0.65)' },
            className: style.slider
          }}
          autoPlay
          onClose={() => setAudioSrc(void 0)}
        />
      </div>,
      document.body
    )
  ) : (
    <></>
  )

  return (
    <PlayerContext.Provider
      value={{
        setAudioSrc,
        setAudioQueue,
        playingRCIDRef
      }}
    >
      {children}
      {playerElement}
    </PlayerContext.Provider>
  )
}

const ChartMaskContentProvider: React.FC<IChartMaskContentProviderProps> = ({ children, maskContent }) => {
  return <ChartMaskContentContext.Provider value={{ maskContent }}>{children}</ChartMaskContentContext.Provider>
}

export {
  AuthConetx,
  AbilityContext,
  MediaContext,
  CompatibleRouteContext,
  PlayerContext,
  ChartMaskContentContext,
  useAuth,
  useAbility,
  useMedia,
  useCompatibleRoute,
  usePlayer,
  useChartMaskContent,
  AuthProvider,
  AbilityProvider,
  MediaProvider,
  CompatibleRouteProvider,
  PlayerProvider,
  ChartMaskContentProvider
}
